import { useMemo } from "react";
import { APP_MAINTENANCE_PATH } from "../../functions/src/admin/paths";
import { currentSchoolKey, DataSnapshot } from "../api/firebase";
import { useDatabase } from "../api/firebaseHooks";
import { Language } from "../api/Lang";
import { getMaintenanceStrings } from "./maintenance-strings";

export type MaintenanceModeState =
    | { status: "loading" }
    | { status: "active"; message: string }
    | { status: "inactive" };

function isTruthyFlag(value: unknown): boolean {
    return value === true || value === "true" || value === 1;
}

function isMaintenanceEnabled(snapshot: DataSnapshot | null | undefined): boolean {
    return isTruthyFlag(snapshot?.child("enabled").val());
}

function isMaintenanceActiveForSchool(snapshot: DataSnapshot | null | undefined, schoolKey: string): boolean {
    if (!isMaintenanceEnabled(snapshot)) {
        return false;
    }
    if (isTruthyFlag(snapshot?.child("allSchools").val())) {
        return true;
    }
    return isTruthyFlag(snapshot?.child("schools").child(schoolKey).val());
}

function getMaintenanceMessage(snapshot: DataSnapshot | null | undefined, lang: Language, fallback: string): string {
    const text = snapshot?.child("message").child(lang).val();
    return (typeof text === "string" && text.trim()) ? text.trim() : fallback;
}

/** Pass `lang` from the same component that owns `useLanguageProvider` (above `langContext.Provider`). */
export function useMaintenanceMode(lang: Language): MaintenanceModeState {
    const strings = getMaintenanceStrings(lang);
    const maintenance = useDatabase(APP_MAINTENANCE_PATH);

    return useMemo((): MaintenanceModeState => {
        if (!maintenance) {
            return { status: "loading" };
        }

        if (!isMaintenanceActiveForSchool(maintenance, currentSchoolKey)) {
            return { status: "inactive" };
        }

        return {
            status: "active",
            message: getMaintenanceMessage(maintenance, lang, strings.defaultMessage),
        };
    }, [maintenance, lang, strings.defaultMessage]);
}
